/**
 * @fileoverview Event Logger Hook
 * 
 * Centralized event tracking system to replace PostHog.
 * Logs user actions and behaviors for analytics.
 * 
 * EVENT CATEGORIES:
 * - navigation: Page views, tab changes
 * - engagement: Saves, shares, votes, reactions
 * - creation: Polls, collections, items created
 * - ai_feature: Chat, try-on, analysis features
 * - commerce: Price alerts, purchases, clicks
 * - social: Follows, comments, messages
 * - auth: Login, signup, logout
 * - settings: Profile updates, preferences
 * 
 * When adding new features, import and use logEvent() with
 * appropriate category and event name.
 */

import { useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

// Event categories for organization
export type EventCategory = 
  | 'navigation'
  | 'engagement'
  | 'creation'
  | 'ai_feature'
  | 'commerce'
  | 'social'
  | 'auth'
  | 'settings'
  | 'error'
  | 'conversion';

export interface EventProperties {
  [key: string]: string | number | boolean | null | undefined;
}

// Generate a session ID that persists for the browser session
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
};

// Queue for batching events
let eventQueue: Array<{
  user_id: string | null;
  event_name: string;
  event_category: EventCategory;
  properties: EventProperties;
  session_id: string;
  device_type: string;
  created_at: string;
}> = [];

let flushTimeout: ReturnType<typeof setTimeout> | null = null;

const flushEvents = async () => {
  if (eventQueue.length === 0) return;
  
  const eventsToFlush = [...eventQueue];
  eventQueue = [];
  
  try {
    const { error } = await supabase.from('events').insert(eventsToFlush);
    if (error) {
      console.error('Failed to flush events:', error);
      // Re-add events to queue on failure (with limit to prevent memory issues)
      if (eventQueue.length < 100) {
        eventQueue = [...eventsToFlush, ...eventQueue];
      }
    }
  } catch (err) {
    console.error('Error flushing events:', err);
  }
};

// Flush events before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (eventQueue.length > 0) {
      // Use sendBeacon for reliable delivery on page unload
      const payload = JSON.stringify(eventQueue);
      navigator.sendBeacon?.(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/events`,
        new Blob([payload], { type: 'application/json' })
      );
    }
  });
}

export function useEventLogger() {
  const { user } = useAuth();
  const sessionId = useRef(getSessionId());
  const deviceType = useRef(getDeviceType());
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Flush events periodically
  useEffect(() => {
    const interval = setInterval(() => {
      flushEvents();
    }, 5000); // Flush every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Heartbeat: emit every 60s while the app is active and user is logged in
  useEffect(() => {
    if (!user?.id) {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    // Send initial heartbeat on mount/login
    const emitHeartbeat = () => {
      eventQueue.push({
        user_id: user.id,
        event_name: 'heartbeat',
        event_category: 'navigation',
        properties: { url: window.location.pathname },
        session_id: sessionId.current,
        device_type: deviceType.current,
        created_at: new Date().toISOString(),
      });
    };

    emitHeartbeat();
    heartbeatRef.current = setInterval(emitHeartbeat, 60_000);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [user?.id]);

  const logEvent = useCallback(
    (
      eventName: string,
      category: EventCategory,
      properties: EventProperties = {}
    ) => {
      const event = {
        user_id: user?.id || null,
        event_name: eventName,
        event_category: category,
        properties: {
          ...properties,
          url: window.location.pathname,
        },
        session_id: sessionId.current,
        device_type: deviceType.current,
        created_at: new Date().toISOString(),
      };

      eventQueue.push(event);

      // Flush immediately if queue is getting large
      if (eventQueue.length >= 10) {
        if (flushTimeout) clearTimeout(flushTimeout);
        flushEvents();
      } else {
        // Schedule flush
        if (flushTimeout) clearTimeout(flushTimeout);
        flushTimeout = setTimeout(flushEvents, 2000);
      }
    },
    [user?.id]
  );

  // Convenience methods for common events
  const logPageView = useCallback(
    (pageName: string, properties?: EventProperties) => {
      logEvent('page_view', 'navigation', { page: pageName, ...properties });
    },
    [logEvent]
  );

  const logButtonClick = useCallback(
    (buttonName: string, properties?: EventProperties) => {
      logEvent('button_click', 'engagement', { button: buttonName, ...properties });
    },
    [logEvent]
  );

  const logFeatureUsed = useCallback(
    (featureName: string, properties?: EventProperties) => {
      logEvent('feature_used', 'ai_feature', { feature: featureName, ...properties });
    },
    [logEvent]
  );

  const logError = useCallback(
    (errorType: string, errorMessage: string, properties?: EventProperties) => {
      logEvent('error_occurred', 'error', { 
        error_type: errorType, 
        error_message: errorMessage.substring(0, 500),
        ...properties 
      });
    },
    [logEvent]
  );

  const logConversion = useCallback(
    (conversionType: string, value?: number, properties?: EventProperties) => {
      logEvent('conversion', 'conversion', { 
        conversion_type: conversionType, 
        value,
        ...properties 
      });
    },
    [logEvent]
  );

  return {
    logEvent,
    logPageView,
    logButtonClick,
    logFeatureUsed,
    logError,
    logConversion,
  };
}

// Standalone function for use outside of React components
export const logEventDirect = async (
  eventName: string,
  category: EventCategory,
  properties: EventProperties = {},
  userId?: string | null
) => {
  try {
    await supabase.from('events').insert({
      user_id: userId || null,
      event_name: eventName,
      event_category: category,
      properties: {
        ...properties,
        url: typeof window !== 'undefined' ? window.location.pathname : null,
      },
      session_id: getSessionId(),
      device_type: getDeviceType(),
    });
  } catch (err) {
    console.error('Failed to log event:', err);
  }
};
